import { type ApiEnvelope, api, unwrap } from "@/lib/api";

export interface AdminUserListItem {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	fullName: string;
	phone: string | null;
	photoUrl: string | null;
	status: "active" | "suspended" | "archived";
	role: { key: string; label: string };
	additionalPermissions: {
		key: string;
		label: string;
		expiresAt: string | null;
	}[];
	assignedCategories: { id: string; name: string | null }[];
	lang: string;
	timezone: string;
	lastLoginAt: string | null;
	lastLoginIp: string | null;
	createdAt: string;
	createdBy: string | null;
	failedAttempts?: number;
	lockedUntil?: string | null;
}

export interface UsersListResponse {
	items: AdminUserListItem[];
	total: number;
	page: number;
	pageSize: number;
}

export interface UsersListParams {
	search?: string;
	role?: string;
	status?: "active" | "suspended" | "archived";
	page?: number;
	pageSize?: number;
}

export interface RoleDef {
	key: string;
	label: string;
	description: string | null;
	sessionTtlMin: number;
	permissions: string[];
}

export interface PermissionDef {
	key: string;
	label: string;
	module: string;
}

export interface CreateUserPayload {
	email: string;
	firstName: string;
	lastName: string;
	phone?: string;
	roleKey: string;
	additionalPermissions?: string[];
	assignedCategoryIds?: string[];
	lang?: string;
}

export interface UpdateUserPayload {
	firstName?: string;
	lastName?: string;
	phone?: string;
	roleKey?: string;
	additionalPermissions?: string[];
	assignedCategoryIds?: string[];
	lang?: string;
}

export interface UserSession {
	id: string;
	userAgent: string | null;
	ip: string | null;
	lastActivityAt: string;
	expiresAt: string;
	createdAt: string;
}

export interface UserAuditEntry {
	id: string;
	at: string;
	actorLabel: string;
	actorRole: string | null;
	action: string;
	category: string;
	target: string | null;
	result: "success" | "failure";
	detail: string | null;
	ip: string | null;
}

export interface UserMfa {
	id: string;
	type: "totp" | "sms" | "email" | "whatsapp";
	verified: boolean;
	isDefault: boolean;
	label: string | null;
	createdAt: string;
}

export const adminUsersApi = {
	list: (params: UsersListParams) => {
		const usp = new URLSearchParams();
		if (params.search) usp.set("search", params.search);
		if (params.role) usp.set("role", params.role);
		if (params.status) usp.set("status", params.status);
		if (params.page) usp.set("page", String(params.page));
		if (params.pageSize) usp.set("pageSize", String(params.pageSize));
		return unwrap<UsersListResponse>(
			api.get<ApiEnvelope<UsersListResponse>>(`/admin/users?${usp.toString()}`),
		);
	},

	rolesPermissions: () =>
		unwrap<{ roles: RoleDef[]; permissions: PermissionDef[] }>(
			api.get<ApiEnvelope<{ roles: RoleDef[]; permissions: PermissionDef[] }>>(
				"/admin/users/roles-permissions",
			),
		),

	get: (id: string) =>
		unwrap<AdminUserListItem>(
			api.get<ApiEnvelope<AdminUserListItem>>(`/admin/users/${id}`),
		),

	sessions: (id: string) =>
		unwrap<UserSession[]>(
			api.get<ApiEnvelope<UserSession[]>>(`/admin/users/${id}/sessions`),
		),

	audit: (id: string) =>
		unwrap<UserAuditEntry[]>(
			api.get<ApiEnvelope<UserAuditEntry[]>>(`/admin/users/${id}/audit`),
		),

	mfa: (id: string) =>
		unwrap<UserMfa[]>(
			api.get<ApiEnvelope<UserMfa[]>>(`/admin/users/${id}/mfa`),
		),

	create: (payload: CreateUserPayload) =>
		unwrap<AdminUserListItem>(
			api.post<ApiEnvelope<AdminUserListItem>>("/admin/users", payload),
		),

	update: (id: string, payload: UpdateUserPayload) =>
		unwrap<AdminUserListItem>(
			api.patch<ApiEnvelope<AdminUserListItem>>(`/admin/users/${id}`, payload),
		),

	suspend: (id: string) =>
		unwrap<AdminUserListItem>(
			api.post<ApiEnvelope<AdminUserListItem>>(`/admin/users/${id}/suspend`),
		),

	reactivate: (id: string) =>
		unwrap<AdminUserListItem>(
			api.post<ApiEnvelope<AdminUserListItem>>(`/admin/users/${id}/reactivate`),
		),

	archive: (id: string) =>
		unwrap<AdminUserListItem>(
			api.post<ApiEnvelope<AdminUserListItem>>(`/admin/users/${id}/archive`),
		),

	resetMfa: (id: string) =>
		unwrap<AdminUserListItem>(
			api.post<ApiEnvelope<AdminUserListItem>>(`/admin/users/${id}/reset-mfa`),
		),

	forceLogout: (id: string) =>
		unwrap<{ revoked: number }>(
			api.post<ApiEnvelope<{ revoked: number }>>(
				`/admin/users/${id}/force-logout`,
			),
		),

	activate: (token: string, password: string) =>
		unwrap<{ ok: true }>(
			api.post<ApiEnvelope<{ ok: true }>>("/auth/activate", {
				token,
				password,
			}),
		),
};
